import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../../../components/Badge';
import Button from '../../../../components/Button';

interface StatusOption {
  code: string;
  name: string;
}

interface HeaderProps {
  stockistName: string;
  period: string;
  status: { code: string; name: string };
  mode: 'view' | 'edit';
  onStatusChange?: (code: string, name: string) => void;
  onDownload?: () => void;
  downloadLoading?: boolean;
}

const STATUS_OPTIONS: StatusOption[] = [
  { code: 'APPROVED', name: 'Approved' },
  { code: 'REJECTED', name: 'Rejected' },
];

const getStatusVariant = (code: string): 'success' | 'danger' | 'warning' | 'info' => {
  switch (code?.toUpperCase()) {
    case 'APPROVED': return 'success';
    case 'REJECTED': return 'danger';
    case 'DRAFT': return 'warning';
    default: return 'info';
  }
};

const Header: React.FC<HeaderProps> = ({
  stockistName,
  period,
  status,
  mode,
  onStatusChange,
  onDownload,
  downloadLoading,
}) => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const variant = getStatusVariant(status?.code);

  return (
    <div className="row mb-6">
      <div className="col-6">
        <h4 className="m-0 fw-bold">{t('monthlyStock.verifyStock.pageTexts.header.titlePrefix')}{stockistName}</h4>
        <p className="mb-0 text-muted">
          {t('monthlyStock.verifyStock.pageTexts.header.statementPeriod')}{period}
        </p>
      </div>

      <div className="col-6 d-flex align-items-center justify-content-end gap-2">
        {onDownload && (
          <span title={t('monthlyStock.verifyStock.pageTexts.header.downloadFile')}>
            <Button
              className="btn btn-sm btn-icon btn-outline-secondary waves-effect"
              icon={<i className="ri-download-2-line ri-18px"></i>}
              onClick={onDownload}
              loading={downloadLoading}
            />
          </span>
        )}

        {mode === 'view' || !onStatusChange ? (
          <Badge text={status?.name} variant={variant} />
        ) : (
          <div className="dropdown">
            <button
              type="button"
              className={`btn btn-sm btn-outline-${variant} dropdown-toggle waves-effect`}
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              {status?.name}
            </button>
            {dropdownOpen && (
              <ul className="dropdown-menu show" style={{ marginTop: 6, right: 0 }}>
                {STATUS_OPTIONS.filter((o) => o.code !== status?.code).map((opt) => (
                  <li key={opt.code}>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        setDropdownOpen(false);
                        onStatusChange(opt.code, opt.name);
                      }}
                    >
                      {opt.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Header;
